import { CheckCircle, Inbox, Mail, MoreHorizontal, Plus, Send, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGetApiMessages } from '@/api/generated/messages';
import type { Message } from '@/api/generated/model/messages';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Local type definition removed in favor of generated model

export default function MessageList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [direction, setDirection] = useState<'INBOUND' | 'OUTBOUND' | 'ALL'>('INBOUND');

  const { data: result, isLoading: loading } = useGetApiMessages({
    direction: direction === 'ALL' ? undefined : (direction as 'INBOUND' | 'OUTBOUND'),
  });
  const messages = result?.data || [];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'UNREAD':
        return (
          <Badge variant="destructive" className="gap-1">
            <Mail className="w-3 h-3" />
            {t('modules.messages.statuses.UNREAD')}
          </Badge>
        );
      case 'READ':
        return (
          <Badge variant="secondary" className="gap-1">
            <CheckCircle className="w-3 h-3" />
            {t('modules.messages.statuses.READ')}
          </Badge>
        );
      case 'REPLIED':
        return (
          <Badge variant="outline" className="gap-1 border-blue-500 text-blue-600">
            <Send className="w-3 h-3" />
            {t('modules.messages.statuses.REPLIED')}
          </Badge>
        );
      case 'CLOSED':
        return (
          <Badge variant="outline" className="gap-1">
            <XCircle className="w-3 h-3" />
            {t('modules.messages.statuses.CLOSED')}
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t('modules.messages.title')}</h1>
          <p className="text-gray-500 mt-1">{t('modules.messages.description')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/settings/message-templates')}>
            {t('modules.messages.templates')}
          </Button>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 shadow-md"
            onClick={() => navigate('/admin/messages/new')}
          >
            <Plus className="w-4 h-4 mr-2" /> {t('modules.messages.compose')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="INBOUND" onValueChange={(v) => setDirection(v as 'INBOUND' | 'OUTBOUND' | 'ALL')}>
        <TabsList>
          <TabsTrigger value="INBOUND" className="gap-2">
            <Inbox className="w-4 h-4" /> {t('modules.messages.inbox')}
          </TabsTrigger>
          <TabsTrigger value="OUTBOUND" className="gap-2">
            <Send className="w-4 h-4" /> {t('modules.messages.sent')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="INBOUND">
          <MessageTable
            messages={messages}
            loading={loading}
            getStatusBadge={getStatusBadge}
            navigate={navigate}
            t={t}
          />
        </TabsContent>
        <TabsContent value="OUTBOUND">
          <MessageTable
            messages={messages}
            loading={loading}
            getStatusBadge={getStatusBadge}
            navigate={navigate}
            t={t}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface MessageTableProps {
  messages: Message[];
  loading: boolean;
  getStatusBadge: (status: string) => React.ReactNode;
  navigate: (path: string) => void;
  t: (key: string) => string;
}

function MessageTable({ messages, loading, getStatusBadge, navigate, t }: MessageTableProps) {
  return (
    <Card className="border-t-4 border-t-indigo-500 shadow-sm mt-4">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">{t('modules.messages.inquiryNumber')}</TableHead>
              <TableHead>{t('modules.messages.subject')}</TableHead>
              <TableHead>{t('modules.messages.status')}</TableHead>
              <TableHead>{t('modules.messages.date')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{renderTableContent()}</TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  function renderTableContent() {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={5} className="text-center py-10 text-gray-400">
            {t('common.loading')}
          </TableCell>
        </TableRow>
      );
    }

    if (messages.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={5} className="text-center py-10 text-gray-400">
            {t('modules.messages.empty')}
          </TableCell>
        </TableRow>
      );
    }

    return messages.map((msg: Message) => (
      <TableRow
        key={msg.id}
        className="group hover:bg-gray-50 cursor-pointer"
        onClick={() => navigate(`/admin/messages/${msg.id}`)}
      >
        <TableCell className="font-mono text-xs text-gray-500">{msg.inquiryNumber || '-'}</TableCell>
        <TableCell className="font-medium">{msg.title}</TableCell>
        <TableCell>{getStatusBadge(msg.status)}</TableCell>
        <TableCell className="text-sm text-gray-600">{new Date(msg.createdAt).toLocaleDateString('ja-JP')}</TableCell>
        <TableCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/messages/${msg.id}`);
                }}
              >
                {t('common.view')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    ));
  }
}
